import type {
  IAccountCredentials,
  IAccountDataResponse,
  IAccountPublic,
  IBulkRegisterRow,
  IBulkRegisterStart,
  ISyncBetLogResult,
  ISyncTransactionResult,
} from '@hit-game/shared/types';

import { http, type Paginated } from '../lib/http';

export interface AccountListParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Chỉ màn Kho tài khoản mới xin mật khẩu, và phải xin tường minh. */
  withPassword?: boolean;
}

/** Account kèm mật khẩu — chỉ có khi gọi với `withPassword`. */
export type AccountWithSecret = IAccountPublic & { password?: string };

/**
 * Thông tin nạp tiền điện tử của một coin.
 *
 * Khai lại ở FE thay vì import từ game-sdk: SDK là gói chạy trên Node (ONNX,
 * Chromium), kéo vào bundle trình duyệt là hỏng build.
 */
export interface ICryptoDepositInfo {
  currency: string;
  /** Tỉ giá nạp: VND cho 1 coin. */
  price: number;
  networks: {
    network: string;
    /** Ví nạp của nhà cái — người dùng chuyển coin vào đây. */
    address: string;
    /** Ảnh QR của `address`, dạng data URI. */
    qr: string;
  }[];
}

export const accountService = {
  list(params: AccountListParams): Promise<Paginated<AccountWithSecret>> {
    return http.get('/accounts', { params });
  },

  detail(id: string): Promise<IAccountPublic> {
    return http.get(`/accounts/${id}`);
  },

  /**
   * Đăng ký tài khoản MỚI ở nhà cái. Bỏ trống username/password → server tự sinh.
   *
   * Mật khẩu trong response là lần DUY NHẤT người dùng thấy được để chép ra —
   * các endpoint khác không trả mật khẩu trừ khi hỏi kèm `withPassword`.
   */
  register(input: {
    /** BẮT BUỘC — toàn bộ request của tài khoản mới đi qua proxy này. */
    proxy: string;
    username?: string;
    password?: string;
    displayName?: string;
  }): Promise<IAccountPublic & { password: string }> {
    return http.post('/accounts/register', input);
  },

  /**
   * Đăng ký hàng loạt (ASYNC) — mỗi proxy đúng MỘT tài khoản, credential do server sinh.
   *
   * Trả NGAY (202) kèm `batchId` + ảnh chụp ban đầu (proxy bị loại là `failed`,
   * proxy hợp lệ là `pending`). login-worker chạy nền — theo dõi tiến độ qua
   * `useBulkRegisterProgress` (SSE) + `bulkRegisterStatus` (đọc kết quả từng dòng).
   */
  bulkRegister(proxies: string[]): Promise<IBulkRegisterStart> {
    // `_silentSuccess`: 202 chỉ là "đã nhận yêu cầu" — toast của nó gây nhiễu vì
    // đã có toast "Tạo thành công N" khi lô xong. Lỗi thì vẫn báo như thường.
    return http.post('/accounts/bulk-register', { proxies }, { _silentSuccess: true });
  },

  /**
   * Tiến độ + kết quả từng dòng của một lô đăng ký.
   *
   * Mật khẩu CHỈ xuất hiện ở dòng `success` và CHỈ ở đây (server join từ Mongo).
   * Gọi sau mỗi ping SSE và theo nhịp poll dự phòng cho tới khi hết dòng `pending`.
   */
  bulkRegisterStatus(batchId: string): Promise<{ rows: IBulkRegisterRow[] }> {
    return http.get(`/accounts/bulk-register/${batchId}`, { _silent: true });
  },

  /** Đăng nhập tài khoản nhà cái rồi thêm vào hệ thống. Chậm — có thể mất chục giây. */
  loginAndAdd(input: {
    username: string;
    password: string;
    proxy?: string;
  }): Promise<IAccountPublic> {
    return http.post('/accounts/login', input);
  },

  /**
   * Mật khẩu + proxy của một tài khoản — cho popup "Thông tin tài khoản".
   *
   * Gọi khi người dùng thực sự mở popup, không nạp sẵn: sảnh poll 30 giây một
   * lần, kéo sẵn mật khẩu của cả kho là để nó bay qua mạng liên tục vô ích.
   */
  credentials(id: string): Promise<IAccountCredentials> {
    return http.get(`/accounts/${id}/credentials`);
  },

  /** Đăng nhập lại để làm mới token + ví. */
  refreshSession(id: string): Promise<IAccountPublic> {
    return http.post(`/accounts/${id}/refresh-session`);
  },

  /**
   * Toàn bộ tài khoản ở shape sảnh dùng — KHÔNG phân trang.
   *
   * Sảnh vẽ mọi tài khoản cùng lúc (chia theo nhóm) nên phân trang chỉ làm mất
   * tài khoản khỏi nhóm của nó.
   */
  overview(): Promise<IAccountDataResponse[]> {
    return http.get('/accounts/overview', { _silent: true });
  },

  /** Đăng nhập nhà cái. CHẬM — captcha + reCAPTCHA + ký browser, có thể chục giây. */
  login(id: string): Promise<IAccountDataResponse> {
    return http.post(`/accounts/${id}/login`);
  },

  logout(id: string): Promise<IAccountDataResponse> {
    return http.post(`/accounts/${id}/logout`);
  },

  /** Làm mới số dư qua verifytoken — rẻ, không captcha. Cần phiên còn sống. */
  refreshBalance(id: string): Promise<IAccountDataResponse> {
    return http.post(`/accounts/${id}/balance`);
  },

  /** Thông tin nạp tiền điện tử, đọc live từ paygate (coin/mạng lưới có thể đổi bất cứ lúc nào). */
  cryptoDeposit(id: string): Promise<ICryptoDepositInfo[]> {
    return http.get(`/accounts/${id}/crypto-deposit`);
  },

  /**
   * Kéo lịch sử cược từ nhà cái về DB rồi mới đọc lại bằng `betlogService.list`.
   *
   * `_silentSuccess`: hai lời gọi này chạy TỰ ĐỘNG lúc mở hộp thoại, người dùng
   * không bấm gì — toast xanh "Đồng bộ xong: 0 bản ghi" mỗi lần mở là nhiễu.
   * Lỗi thì vẫn báo, vì lúc đó bảng đứng im ở bản cũ và cần nói rõ tại sao.
   */
  syncBetLogs(id: string): Promise<ISyncBetLogResult> {
    return http.post(`/betlogs/${id}/sync`, undefined, { _silentSuccess: true });
  },

  syncTransactions(id: string): Promise<ISyncTransactionResult> {
    return http.post(`/transactions/${id}/sync`, undefined, { _silentSuccess: true });
  },

  assignProxy(id: string, proxy: string | null): Promise<null> {
    return http.put(`/accounts/${id}/proxy`, { proxy });
  },

  remove(id: string): Promise<null> {
    return http.delete(`/accounts/${id}`);
  },
};
